// ============================================================================
// OnboardingDetail — right-side slide-over. Full 16-item checklist by section
// (applies_to-filtered), tap-to-cycle status with optimistic checklist_json
// writes, per-item notes, and a sticky right rail (Move to Stage / Send
// Reminder / Add Note / Cancel Onboarding). Lineage chip for hiring-sourced
// records. Spec §2 (detail).
// ============================================================================
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, GitBranch, StickyNote, UserPlus, RotateCcw, FileText } from 'lucide-react';
import { Button, useToast } from '../../shared/components';
import { PacketLaunchModal } from '../esign/packet-launch-modal';
import { usePacketLaunchOptions } from '../esign/use-packet-launch';
import { defaultPacketFor, launchBlockers } from '../esign/packet-launch';
import {
  createEmployeeFromOnboarding,
  parseHireNameFromNotes,
} from './hooks/use-onboarding-data';
import {
  effectiveChecklist,
  computeProgress,
  mergeChecklistIntoRaw,
  visibleItems,
  fmtDate,
  withItemStatus,
  appendNote,
} from './utils';
import {
  ONBOARDING_STAGES,
  stageLabel,
  stageName,
  checklistLabel,
  CHECKLIST_SECTIONS,
  SECTION_LABELS,
  TYPE_CHIP_LABELS,
  type CanonicalChecklist,
  type ChecklistStatus,
  type OnboardingRecord,
  type OnboardingStage,
} from './types';
import ReminderModal from './reminder-modal';
import PartnerDocumentsSection from './partner-documents-section';
import PortalLinkSection from './portal-link-section';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { ownerLabel } from '../../shared/position-owner';

const NEXT_STATUS: Record<ChecklistStatus, ChecklistStatus> = {
  pending: 'complete',
  complete: 'blocked',
  blocked: 'n_a',
  n_a: 'pending',
};

const STATUS_PILL: Record<ChecklistStatus, { label: string; cls: string }> = {
  complete: { label: '✅ Complete', cls: 'bg-st-ok-bg-strong text-st-ok-fg' },
  pending: { label: '❌ Pending', cls: 'bg-st-danger-bg-strong text-st-danger-fg' },
  blocked: { label: '⏳ Blocked', cls: 'bg-st-warn-bg-strong text-st-warn-fg' },
  n_a: { label: '— N/A', cls: 'bg-tcs-surface text-tcs-text-muted' },
};

interface Props {
  rec: OnboardingRecord;
  /** employee_id -> display name, for the optional named assignee (TCSD-0162). */
  ownerNames?: ReadonlyMap<string, string>;
  canWrite: boolean;
  onPatch: (
    rec: OnboardingRecord,
    dbPatch: Record<string, unknown>,
    localPatch: Partial<OnboardingRecord>,
  ) => Promise<void>;
  onClose: () => void;
  // Refresh the parent list after a structural change (e.g. employee created
  // and linked) so the new employee_id / name flows back into the board.
  onRefetch?: () => void;
}

export default function OnboardingDetail({ rec, ownerNames, canWrite, onPatch, onClose, onRefetch }: Props) {
  const toast = useToast();
  const navigate = useNavigate();
  const [reminderOpen, setReminderOpen] = useState(false);
  const [noteOpenKey, setNoteOpenKey] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);

  // AFB-0075 / PR 5 — the onboarding packet. The rail has to know whether any
  // active packet applies to this person BEFORE it draws the button, because a
  // disabled control with no reason on screen is the same as a broken one.
  // Loaded once and never re-run: the picker holds the sender's per-send
  // choices, and a loader firing under it is how typed state gets destroyed.
  const packets = usePacketLaunchOptions(rec.source === 'employee');
  const packetReasons = launchBlockers(
    {
      source: rec.source,
      employee_id: rec.employee_id,
      start_date: rec.start_date,
      workerClass: rec.employmentTypeForChecklist,
      canWrite,
    },
    packets.options,
  );
  const defaultPacket = defaultPacketFor(packets.options, rec.employmentTypeForChecklist);
  const packetBlocked = packets.loading || packets.error !== null || packetReasons.length > 0;
  const packetNote = packets.loading
    ? 'Checking which packets apply to this person…'
    : packets.error
      ? packets.error
      : packetReasons.length > 0
        ? packetReasons.join(' ')
        : `Builds a draft ${defaultPacket?.name ?? 'onboarding'} packet and opens it for ` +
          'review. Nothing is emailed until you send it from there.';

  // Promote-to-employee is only meaningful for the employee track, and only
  // when this onboarding hasn't already produced an employee.
  const canPromote = canWrite && rec.source === 'employee' && !rec.employee_id;

  const checklist = useMemo(() => effectiveChecklist(rec.checklist), [rec.checklist]);
  const type = rec.employmentTypeForChecklist;
  const progress = computeProgress(checklist, type);

  // Persist a new checklist (merging into the raw blob to preserve metadata).
  async function writeChecklist(next: CanonicalChecklist) {
    const merged = mergeChecklistIntoRaw(rec.rawChecklistJson, next);
    try {
      await onPatch(rec, { checklist_json: merged }, { checklist: next, rawChecklistJson: merged });
    } catch (e) {
      toast.error('Could not save checklist', {
        description: errorMessage(e, 'Permission denied.'),
      });
    }
  }

  function cycleItem(section: (typeof CHECKLIST_SECTIONS)[number], key: string) {
    if (!canWrite) return;
    const item = checklist[section].find((it) => it.key === key);
    if (!item) return;
    writeChecklist(withItemStatus(checklist, key, NEXT_STATUS[item.status]));
  }

  function setItemNote(section: (typeof CHECKLIST_SECTIONS)[number], key: string, note: string) {
    const next: CanonicalChecklist = {
      documents: [...checklist.documents],
      compliance: [...checklist.compliance],
      training: [...checklist.training],
      admin: [...checklist.admin],
    };
    next[section] = next[section].map((it) => (it.key === key ? { ...it, notes: note } : it));
    writeChecklist(next);
  }

  async function moveStage(next: OnboardingStage) {
    try {
      await onPatch(rec, { status: next }, { rawStatus: next, stage: next, terminal: false });
      toast.success(`Moved to ${stageLabel(next, rec.type)}`);
    } catch (e) {
      toast.error('Could not move stage', {
        description: errorMessage(e, 'Permission denied.'),
      });
    }
  }

  async function addNote() {
    const text = window.prompt('Add a note (appended to the record notes):');
    if (!text) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const nextNotes = appendNote(rec.notes, text, stamp);
    try {
      await onPatch(rec, { notes: nextNotes }, { notes: nextNotes });
      toast.success('Note added');
    } catch (e) {
      toast.error('Could not add note', { description: errorMessage(e, '') });
    }
  }

  // Item 871 follow-on (Ben, 2026-10-21): a sub or partner card says "setup";
  // a W2 card keeps "onboarding". Words only.
  const word = rec.type === 'W2' ? 'onboarding' : 'setup';
  const Word = rec.type === 'W2' ? 'Onboarding' : 'Setup';

  async function cancelOnboarding() {
    try {
      await onPatch(rec, { status: 'Cancelled' }, { rawStatus: 'Cancelled', terminal: true });
      toast.success(`${Word} archived`);
      setConfirmCancel(false);
      onClose();
    } catch (e) {
      toast.error('Could not archive', { description: errorMessage(e, '') });
    }
  }

  // Restore a cancelled/terminal onboarding back onto the active board.
  async function restoreOnboarding() {
    try {
      await onPatch(
        rec,
        { status: 'Offer Accepted' },
        { rawStatus: 'Offer Accepted', stage: 'Offer Accepted', terminal: false },
      );
      toast.success(`${Word} restored`);
      onClose();
    } catch (e) {
      toast.error('Could not restore', { description: errorMessage(e, '') });
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end bg-tcs-scrim/60"
      role="dialog"
      aria-modal="true"
      aria-label={`${Word} detail — ${rec.name}`}
      {...overlayClickClose(onClose)}
    >
      <div
        className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-tcs-raised shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="border-b border-tcs-border px-5 py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-lg font-semibold text-tcs-text">{rec.name}</h2>
                <span className="shrink-0 rounded-full bg-tcs-surface px-2 py-0.5 text-[10px] font-medium text-tcs-text-secondary">
                  {TYPE_CHIP_LABELS[rec.type]}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-tcs-text-muted">
                {[rec.role, rec.account].filter(Boolean).join(' · ') || '—'}
              </div>
              {rec.hiring_request_id && (
                <span className="mt-1 inline-flex items-center gap-1 rounded bg-st-info-bg px-1.5 py-0.5 text-[10px] text-st-info-fg">
                  <GitBranch className="h-3 w-3" /> From hiring request {rec.hiring_request_id}
                </span>
              )}
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-tcs-surface">
              <X className="h-5 w-5" />
            </button>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-tcs-text-muted sm:grid-cols-4">
            <Meta label="Start" value={fmtDate(rec.start_date)} />
            <Meta label="Stage" value={stageLabel(rec.stage, rec.type)} />
            {/* TCSD-0162 — the POSITION owns it; a named assignee is shown after it,
                never instead of it. ⛔ Do not render assigned_to_eid on its own. */}
            <Meta label="Owner" value={ownerLabel(rec, ownerNames)} />
            <Meta label="AM" value={rec.assigned_am ?? '—'} />
            <Meta label="Created" value={fmtDate(rec.created_at?.slice(0, 10))} />
            <Meta label="Updated" value={fmtDate(rec.updated_at?.slice(0, 10))} />
            <Meta label="Progress" value={`${progress}%`} />
            <Meta label="Type" value={stageName(rec.rawStatus, rec.type) ?? '—'} />
          </dl>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Checklist */}
          <div className="flex-1 overflow-y-auto p-5">
            {!canWrite && (
              <div className="mb-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
                Read-only — your role can view but not edit {word} records.
              </div>
            )}
            {rec.checklist === null && (
              <div className="mb-3 rounded-md border border-tcs-border bg-tcs-surface px-3 py-2 text-[11px] text-tcs-text-muted">
                No canonical checklist on this record yet — showing the standard 16-item seed.
                Editing an item writes the checklist back (preserving any existing metadata).
              </div>
            )}
            {CHECKLIST_SECTIONS.map((section) => {
              const items = visibleItems(checklist[section], type);
              if (items.length === 0) return null;
              return (
                <section key={section} className="mb-5">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
                    {SECTION_LABELS[section]}
                  </h3>
                  <ul className="space-y-1.5">
                    {items.map((it) => (
                      <li key={it.key} className="rounded-md border border-tcs-border px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 text-sm text-tcs-text">{checklistLabel(it.key, it.label, type)}</span>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              disabled={!canWrite}
                              onClick={() => cycleItem(section, it.key)}
                              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[it.status].cls} ${
                                canWrite ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                              }`}
                              title={canWrite ? 'Tap to cycle status' : 'Read-only'}
                            >
                              {STATUS_PILL[it.status].label}
                            </button>
                            <button
                              type="button"
                              onClick={() => setNoteOpenKey(noteOpenKey === it.key ? null : it.key)}
                              aria-label="Toggle note"
                              className={`rounded p-1 hover:bg-tcs-surface ${it.notes ? 'text-st-info-fg' : 'text-tcs-text-muted'}`}
                            >
                              <StickyNote className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {noteOpenKey === it.key && (
                          <textarea
                            defaultValue={it.notes ?? ''}
                            placeholder="Per-item note…"
                            disabled={!canWrite}
                            onBlur={(e) => {
                              if (canWrite && e.target.value !== (it.notes ?? '')) {
                                setItemNote(section, it.key, e.target.value);
                              }
                            }}
                            rows={2}
                            className="mt-2 w-full resize-y rounded border border-tcs-border bg-tcs-bg px-2 py-1 text-xs"
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {/* AFB-0011 — partner document repository (subs/partners only). */}
            {rec.provider_id && (
              <PartnerDocumentsSection
                providerId={rec.provider_id}
                onboardingId={rec.onboarding_id}
              />
            )}

            {/* N3 — sub onboarding portal link (partner records only). */}
            {rec.source === 'partner' && (
              <PortalLinkSection
                onboardingId={rec.onboarding_id}
                canWrite={canWrite}
              />
            )}
          </div>

          {/* Right rail */}
          <aside className="hidden w-56 shrink-0 border-l border-tcs-border p-4 sm:block">
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-tcs-text-secondary">
                  Move to stage
                </label>
                <select
                  value={rec.terminal ? '' : rec.stage}
                  disabled={!canWrite}
                  onChange={(e) => e.target.value && moveStage(e.target.value as OnboardingStage)}
                  className="w-full rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5 text-xs disabled:opacity-50"
                >
                  {rec.terminal && <option value="">{rec.rawStatus ?? 'Terminal'}</option>}
                  {ONBOARDING_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {stageLabel(s, rec.type)}
                    </option>
                  ))}
                </select>
              </div>
              <Button size="sm" variant="secondary" className="w-full" onClick={() => setReminderOpen(true)}>
                Send reminder
              </Button>
              <Button size="sm" variant="ghost" className="w-full" disabled={!canWrite} onClick={addNote}>
                Add note
              </Button>

              {/* AFB-0075 — the onboarding packet. Disabled is always
                  EXPLAINED: the sentence underneath names the missing fact and
                  where to fix it, because a greyed-out button that says nothing
                  is indistinguishable from a broken one. */}
              <div className="rounded-md border border-tcs-border p-2">
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full"
                  disabled={packetBlocked}
                  icon={<FileText className="h-3.5 w-3.5" />}
                  onClick={() => setPacketOpen(true)}
                >
                  Generate onboarding packet
                </Button>
                <p className="mt-1 text-[10px] leading-tight text-tcs-text-muted">
                  {packetNote}
                </p>
              </div>

              {/* Onboarding → Employee (H2). Either the create CTA, or a chip
                  showing the linked employee once one exists. */}
              {rec.source === 'employee' && (
                <div className="rounded-md border border-tcs-border p-2">
                  {rec.employee_id ? (
                    <div className="text-[11px] text-tcs-text-muted">
                      Linked to employee{' '}
                      <Link
                        to={`/people/hr/directory/${rec.employee_id}`}
                        className="font-medium text-st-info-fg underline"
                      >
                        {rec.employee_id}
                      </Link>
                    </div>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        className="w-full"
                        disabled={!canPromote}
                        icon={<UserPlus className="h-3.5 w-3.5" />}
                        onClick={() => setPromoteOpen(true)}
                      >
                        Create employee record
                      </Button>
                      <p className="mt-1 text-[10px] leading-tight text-tcs-text-muted">
                        Creates a linked employee. Onboarding stays open so the
                        hire can work while the checklist finishes.
                      </p>
                    </>
                  )}
                </div>
              )}

              {!rec.terminal ? (
                <Button
                  size="sm"
                  variant="destructive"
                  className="w-full"
                  disabled={!canWrite}
                  onClick={() => setConfirmCancel(true)}
                >
                  Archive {word}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full"
                  disabled={!canWrite}
                  icon={<RotateCcw className="h-3.5 w-3.5" />}
                  onClick={restoreOnboarding}
                >
                  Restore {word}
                </Button>
              )}
            </div>
          </aside>
        </div>
      </div>

      {reminderOpen && <ReminderModal records={[rec]} onClose={() => setReminderOpen(false)} />}

      {/* Rendered here, as a sibling of the other overlays, so nothing in the
          checklist above can re-render it out from under the sender's ticks.
          On success the route changes to the review — that IS the landing
          surface, and nothing has been emailed at that point. */}
      {packetOpen && (
        <PacketLaunchModal
          onboardingId={rec.onboarding_id}
          personName={rec.name}
          workerClass={rec.employmentTypeForChecklist}
          options={packets.options}
          onClose={() => setPacketOpen(false)}
          onGenerated={(envelopeId) => {
            setPacketOpen(false);
            toast.success('Packet built as a draft — review it before sending');
            navigate(`/money/envelopes/${envelopeId}`);
          }}
        />
      )}

      {confirmCancel && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-tcs-scrim/60 p-4"
          {...overlayClickClose(() => setConfirmCancel(false))}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-tcs-overlay p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold text-tcs-text">Cancel this {word}?</div>
            <p className="mt-1 text-xs text-tcs-text-muted">
              Sets status to Cancelled and removes the card from the active board. This can be
              reversed by moving it back to a stage.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>
                Keep it
              </Button>
              <Button size="sm" variant="destructive" onClick={cancelOnboarding}>
                Archive {word}
              </Button>
            </div>
          </div>
        </div>
      )}

      {promoteOpen && (
        <PromoteDialog
          rec={rec}
          onClose={() => setPromoteOpen(false)}
          onDone={(employeeId) => {
            setPromoteOpen(false);
            toast.success(`Employee ${employeeId} created and linked`);
            onRefetch?.();
            onClose();
          }}
        />
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-tcs-text-muted">{label}</dt>
      <dd className="font-medium text-tcs-text">{value}</dd>
    </div>
  );
}

// Confirmation + light pre-filled form for Onboarding → Employee (H2). Name is
// pre-filled from the handoff-stamped notes; HR confirms/corrects before the
// employee row is created. Employment type defaults to the record's type.
function PromoteDialog({
  rec,
  onClose,
  onDone,
}: {
  rec: OnboardingRecord;
  onClose: () => void;
  onDone: (employeeId: string) => void;
}) {
  const parsed = parseHireNameFromNotes(rec.notes);
  const [first, setFirst] = useState(parsed.first);
  const [last, setLast] = useState(parsed.last);
  const [empType, setEmpType] = useState(rec.type === 'W2' ? 'W2' : 'Subcontractor');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    if (!first.trim() && !last.trim()) {
      setError('Enter the new employee’s name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { employee_id } = await createEmployeeFromOnboarding(rec, {
        firstName: first,
        lastName: last,
        employmentType: empType,
      });
      onDone(employee_id);
    } catch (e) {
      setError(errorMessage(e, 'Could not create employee.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div className="w-full max-w-sm rounded-xl bg-tcs-overlay p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold text-tcs-text">Create employee record</div>
        <p className="mt-1 text-xs text-tcs-text-muted">
          Creates an <code>employees</code> row and links it to this onboarding. The
          onboarding stays active so it can finish independently.
        </p>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">First</span>
              <input value={first} onChange={(e) => setFirst(e.target.value)} className={pInputCls} autoFocus />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">Last</span>
              <input value={last} onChange={(e) => setLast(e.target.value)} className={pInputCls} />
            </label>
          </div>
          <label className="block text-xs">
            <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">Employment type</span>
            <select value={empType} onChange={(e) => setEmpType(e.target.value)} className={pInputCls}>
              <option value="W2">W2</option>
              <option value="Subcontractor">Subcontractor</option>
            </select>
          </label>
        </div>
        {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={go} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create & link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const pInputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm focus:border-tcs-border-input focus:outline-none';
